import { useStaticQueryWithPreview } from "@tedgustaf/gatsby-plugin-sanity-preview";
import { graphql, useStaticQuery } from "gatsby";
import { DateTime } from "luxon";
import React, { useContext } from "react";
import { PageContext } from "../../context/PageContext";
import { investorRelationsPreviewQuery } from "../../preview/staticQueries/investorRelations";
import { compareLanguages } from "../../utilities/routes/utilities";
import CalendarCard from "./CalendarCard";
import LatestReportCard from "./LatestReportCard";
import PressReleasesCard from "./PressReleases";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Node = Record<string, any>;

export type InvestorRelationsQueryResult = {
  calendarEvents?: {
    nodes?: Node[];
  };
  financialCalendars: {
    nodes?: Node[];
  };
  pressReleaseLists: {
    nodes?: Node[];
  };
  pressReleases: {
    nodes?: Node[];
  };
  reports: {
    nodes?: Node[];
  };
};

export interface InvestorRelationsProps {
  pressReleasesTitle?: string;
  calendarTitle?: string;
  showPressReleases?: boolean;
  showLatestReport?: boolean;
  showCalendarEvents?: boolean;
  maxPressReleases?: number;
  maxCalendarEvents?: number;
}

const InvestorRelations = (props: InvestorRelationsProps) => {
  const {
    calendarTitle,
    pressReleasesTitle,
    showPressReleases = true,
    showLatestReport = true,
    showCalendarEvents = true,
    maxPressReleases = 3,
    maxCalendarEvents = 3
  } = props;

  const pageContext = useContext(PageContext);

  const data = useStaticQueryWithPreview({
    staticQueryData: useStaticQuery<InvestorRelationsQueryResult>(graphql`
      fragment PressReleaseFragment on SanityPressRelease {
        _type
        title
        slug {
          current
        }
        teaserDescription
        publishDate
        i18nLang
      }

      query InvestorRelationsQuery {
        pressReleases: allSanityPressRelease(
          filter: { slug: { current: { ne: null } }, pressReleaseType: { ne: "report" } }
          sort: { publishDate: DESC }
        ) {
          nodes {
            ...PressReleaseFragment
          }
        }

        calendarEvents: allSanityCalendarEvent(
          filter: { startDate: { ne: null } }
          sort: { startDate: ASC }
        ) {
          nodes {
            ...SanityCalendarEventFragment
          }
        }

        reports: allSanityPressRelease(
          filter: { slug: { current: { ne: null } }, pressReleaseType: { eq: "report" } }
          sort: { publishDate: DESC }
        ) {
          nodes {
            ...PressReleaseFragment
          }
        }

        pressReleaseLists: allSanityPressReleaseList(
          filter: { slug: { current: { ne: null } } }
        ) {
          nodes {
            slug {
              current
            }
            i18nLang
          }
        }

        financialCalendars: allSanityFinancialCalendar(
          filter: { slug: { current: { ne: null } } }
        ) {
          nodes {
            slug {
              current
            }
            i18nLang
          }
        }
      }
    `),
    query: investorRelationsPreviewQuery
  });

  const jsx: JSX.Element[] = [];
  const pressReleaseList = data.pressReleaseLists?.nodes
    ?.filter(item => compareLanguages(item.i18nLang, pageContext.i18nLang))
    .find(_ => true);

  if (showPressReleases) {
    const latestPressReleases =
      data.pressReleases?.nodes
        ?.filter(item => compareLanguages(item.i18nLang, pageContext.i18nLang))
        .slice(0, maxPressReleases) ?? [];
    jsx.push(
      <PressReleasesCard
        archiveLinkReference={pressReleaseList}
        items={latestPressReleases}
        key={jsx.length}
        title={pressReleasesTitle}
      />
    );
  }
  if (showLatestReport) {
    const report = data.reports?.nodes
      ?.filter(item => compareLanguages(item.i18nLang, pageContext.i18nLang))
      .find(_ => true);
    jsx.push(
      <LatestReportCard
        key={jsx.length}
        report={report}
        archiveLinkReference={pressReleaseList}
      />
    );
  }
  if (showCalendarEvents) {
    const events =
      data.calendarEvents?.nodes
        ?.filter(event => {
          let dateValid: boolean;
          if (event.endDate?.length) {
            dateValid = DateTime.fromISO(event.endDate).diffNow("hours").hours > 0;
          } else {
            dateValid = DateTime.fromISO(event.startDate).diffNow("hours").hours > 0;
          }

          return dateValid && compareLanguages(event.i18nLang, pageContext.i18nLang);
        })
        .slice(0, maxCalendarEvents) ?? [];

    const financialCalendar = data.financialCalendars?.nodes
      ?.filter(item => compareLanguages(item.i18nLang, pageContext.i18nLang))
      .find(_ => true);

    jsx.push(
      <CalendarCard
        archiveLinkReference={financialCalendar}
        events={events}
        key={jsx.length}
        title={calendarTitle}
      />
    );
  }

  return (
    <aside
      className={`grid grid-cols-1 xl:grid-cols-${jsx.length} ${
        jsx.length === 2 ? "gap-16" : "gap-12"
      }`}
    >
      {jsx}
    </aside>
  );
};

export default InvestorRelations;
